<script setup>
import { computed } from 'vue';
import { useAdminStore } from '@/store/AdminStore';

const adminStore = useAdminStore();
const stats = computed(() => adminStore.systemStats);
const isLoading = computed(() => adminStore.isLoading);

const statCards = computed(() => [
  {
    title: 'Total Users',
    value: stats.value.total_users || 0,
    icon: 'fas fa-users',
    gradient: 'from-cyan',
    glow: 'cyan',
  },
  {
    title: 'Total Sensors',
    value: stats.value.total_sensors || 0,
    icon: 'fas fa-satellite-dish',
    gradient: 'from-purple',
    glow: 'purple',
  },
  {
    title: 'Sensor Types',
    value: stats.value.total_sensor_types || adminStore.sensorTypes.length,
    icon: 'fas fa-microchip',
    gradient: 'from-emerald',
    glow: 'emerald',
  },
  {
    title: 'Active Alerts',
    value: stats.value.active_alerts || 0,
    icon: 'fas fa-bell',
    gradient: 'from-amber',
    glow: 'amber',
  },
]);
</script>

<template>
  <div class="stats-grid">
    <div v-for="(stat, index) in statCards" :key="index" class="stat-card" :class="stat.gradient" :style="{ '--delay': `${index * 0.1}s` }">
      <div class="stat-bg"></div>
      <div class="stat-content">
        <div class="stat-icon" :class="stat.glow">
          <i :class="stat.icon"></i>
        </div>
        <div class="stat-info">
          <span class="stat-label">{{ stat.title }}</span>
          <div v-if="!isLoading" class="stat-value">{{ stat.value.toLocaleString() }}</div>
          <div v-else class="stat-skeleton"></div>
        </div>
      </div>
      <div class="stat-decoration">
        <div class="decoration-line"></div>
        <div class="decoration-dot"></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  position: relative;
  border-radius: 16px;
  padding: 1.5rem;
  overflow: hidden;
  animation: cardAppear 0.5s ease forwards;
  animation-delay: var(--delay);
  opacity: 0;
  transform: translateY(20px);
}

@keyframes cardAppear {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-bg {
  position: absolute;
  inset: 0;
  background: rgba(20, 20, 30, 0.7);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  transition: all 0.3s ease;
}

.stat-card:hover .stat-bg {
  border-color: rgba(255, 255, 255, 0.15);
}

/* Gradient variations */
.stat-card.from-cyan .stat-bg { 
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(20, 20, 30, 0.9) 100%);
}
.stat-card.from-purple .stat-bg { 
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(20, 20, 30, 0.9) 100%);
}
.stat-card.from-emerald .stat-bg { 
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(20, 20, 30, 0.9) 100%);
}
.stat-card.from-amber .stat-bg { 
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(20, 20, 30, 0.9) 100%);
}

.stat-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.stat-icon.cyan {
  background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
  box-shadow: 0 8px 20px rgba(0, 212, 255, 0.3);
}
.stat-icon.purple {
  background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
  box-shadow: 0 8px 20px rgba(124, 58, 237, 0.3);
}
.stat-icon.emerald {
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
}
.stat-icon.amber {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  box-shadow: 0 8px 20px rgba(245, 158, 11, 0.3);
}

.stat-card:hover .stat-icon {
  transform: scale(1.05);
}

.stat-icon i {
  font-size: 1.5rem;
  color: white;
}

.stat-info {
  flex: 1;
}

.stat-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #6b6b7b;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #f0f0f5;
  line-height: 1;
}

.stat-skeleton {
  height: 2rem;
  width: 60px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.stat-skeleton {
  background: linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%);
  background-size: 200% 100%;
}

/* Decoration elements */
.stat-decoration {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.decoration-line {
  width: 30px;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1px;
}

.decoration-dot {
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
}

.stat-card.from-cyan .decoration-dot { background: rgba(0, 212, 255, 0.5); }
.stat-card.from-purple .decoration-dot { background: rgba(124, 58, 237, 0.5); }
.stat-card.from-emerald .decoration-dot { background: rgba(16, 185, 129, 0.5); }
.stat-card.from-amber .decoration-dot { background: rgba(245, 158, 11, 0.5); }

/* Responsive */
@media (max-width: 576px) {
  .stat-card {
    padding: 1.25rem;
  }
  
  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }
  
  .stat-icon i {
    font-size: 1.25rem;
  }
  
  .stat-value {
    font-size: 1.5rem;
  }
}
</style>
